import {createApp, defineAsyncComponent} from 'vue';

import App from '@/App.vue';

import '@assets/svg_icons/fygr-icons.scss';
import '@/assets/style.scss';
import '@/assets/fonts.scss';
import 'v-calendar/style.css';
import FontAwesomeIcon from '@/plugins/FontAwesome';
import SegmentAnalytics from '@/plugins/SegmentAnalytics';
import BasePopup from '@/components/elements/popups/BasePopup.vue';
import {createGtm} from '@gtm-support/vue-gtm';
import router from '@/router/router';
import store from '@/store';
import {vueErrorHandler} from '@/utils/errorHandler.js';
import {registerSW} from 'virtual:pwa-register';

const VueObserveVisibility = () => import('vue3-observe-visibility');

const app = createApp({
  extends: App,
})
  .use(router)
  .use(store)
  .use(VueObserveVisibility)
  .use(SegmentAnalytics)
  .component('FontAwesomeIcon', FontAwesomeIcon)
  .component('BasePopup', BasePopup)
  .component(
    'FygrSelectableTable',
    defineAsyncComponent(
      () => import('@/components/elements/FygrSelectableTable.vue'),
    ),
  )
  .component(
    'FygrButton',
    defineAsyncComponent(() => import('@/components/elements/FygrButton.vue')),
  )
  .component(
    'IconButton',
    defineAsyncComponent(() => import('@/components/elements/IconButton.vue')),
  )
  .component(
    'FygrInput',
    defineAsyncComponent(() => import('@/components/elements/FygrInput.vue')),
  )
  .component(
    'LuPlaceholder',
    defineAsyncComponent(
      () => import('@/components/elements/LuPlaceholder.vue'),
    ),
  )
  .component(
    'Checkbox',
    defineAsyncComponent(() => import('@/components/elements/Checkbox.vue')),
  )
  .component(
    'Toggle',
    defineAsyncComponent(() => import('@/components/elements/Toggle.vue')),
  )
  .component(
    'RangedInput',
    defineAsyncComponent(() => import('@/components/elements/RangedInput.vue')),
  )
  .component(
    'Spinner',
    defineAsyncComponent(() => import('@/components/elements/Spinner.vue')),
  )
  .component(
    'Popup',
    defineAsyncComponent(() => import('@/components/elements/Popup.vue')),
  )
  .component(
    'Loader',
    defineAsyncComponent(() => import('@/components/elements/Loader.vue')),
  )
  .component(
    'Container',
    defineAsyncComponent(() => import('@/components/elements/Container.vue')),
  )
  .component(
    'NoNavigation',
    defineAsyncComponent(
      () => import('@/components/layouts/NoNavigationLayout.vue'),
    ),
  )
  .component(
    'NoAuthLayout',
    defineAsyncComponent(() => import('@/components/layouts/NoAuthLayout.vue')),
  )
  .component(
    'EmptyLayout',
    defineAsyncComponent(() => import('@/components/layouts/EmptyLayout.vue')),
  )
  .component(
    'SkeletonPreloader',
    defineAsyncComponent(
      () => import('@/components/elements/SkeletonPreloader.vue'),
    ),
  )
  .mixin({
    mounted() {
      this.$el.__vue__ = this;
    },
  });
if (import.meta.env.VITE_APP_ENV === 'live') {
  app.config.errorHandler = vueErrorHandler;
}
app.use(
  createGtm({
    id: 'GTM-565JZRV',
    loadScript: false,
    vueRouter: router,
    vueRouterAdditionalEventData: () => {
      const store = app.config.globalProperties.$store;
      const props =
        store?.state?.user?.profileData?.advancedConversionProperties || {};
      return {
        user_data: props,
      };
    },
  }),
);

const updateSW = registerSW({
  // for PWA update
  onNeedRefresh() {
    updateSW(true);
  },
});

app.mount('#app');

store.commit('user/initStorage', null, {root: true});

// mandatory: weglot can be initialize but not ready to switch
// If we go in 'actionGetUserPreferences' before switchersReady, this listener will allow to synchro weglot with store
// If we listen 'switchersReady' before 'actionGetUserPreferences', the 'actionGetUserPreferences' will work correctly
function initWeglot(retries = 10) {
  if (window.Weglot && typeof window.Weglot.on === 'function') {
    window.Weglot.on('switchersReady', function () {
      const userLocale = store.getters['user/getLocale'];
      const fallbackLocale = window.Weglot.getBestAvailableLanguage?.();
      window.Weglot.switchTo(userLocale || fallbackLocale);
    });

    window.Weglot.initialize({
      api_key:
        import.meta.env.VITE_APP_ENV === 'live'
          ? 'wg_8ab3aff22b808085d046f8eebec9f4a42'
          : 'wg_f1ae7d22c57dbf2ab34503156d75c9668',
      switchers: null,
      cache: true,
      whitelist: [
        {
          value: '.translate',
        },
      ],
    });
  } else if (retries > 0) {
    setTimeout(() => initWeglot(retries - 1), 150);
  } else {
    console.error('Weglot not available after retries - main.js');
  }
}
initWeglot();

window.utils = {
  genId: () => {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  },
};

const wg = JSON.parse(localStorage.getItem('wg-translations'));
if (wg && wg['gt4543-clean-weglot-mess'] !== true) {
  for (const key in wg) {
    if (wg[key] && wg[key].createdTime <= 1714010400000) {
      localStorage.removeItem('wg-translations');
      localStorage.setItem(
        'wg-translations',
        JSON.stringify({'gt4543-clean-weglot-mess': true}),
      );
      break;
    }
  }
}
